import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { SetURLSearchParams } from 'react-router-dom';
import type { SearchResponse } from '../domain';

interface SidebarProps {
  data: SearchResponse;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

interface FilterFields {
  priceMin: string;
  priceMax: string;
  bedrooms: string;
  [key: string]: string | string[];
}

export function Sidebar({ data, searchParams, setSearchParams }: SidebarProps) {
  const { register, handleSubmit, reset } = useForm<FilterFields>({
    defaultValues: {
      priceMin: searchParams.get('priceMin') ?? '',
      priceMax: searchParams.get('priceMax') ?? '',
      bedrooms: searchParams.get('bedrooms') ?? 'any',
    },
  });

  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = useCallback((typeName: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [typeName]: !prev[typeName] }));
  }, []);

  // Re-sync form state whenever new search results arrive.
  //
  // IMPORTANT: facet fields are keyed by plain typeName (e.g. "CITY_STATE"),
  // NOT "CITY_STATE[]". react-hook-form's internal path parser treats "[]" as
  // array-index syntax, so registering/reading a field named "CITY_STATE[]"
  // silently puts the value at a different path and formData["CITY_STATE[]"]
  // always returns undefined — preventing any URL param from being appended and
  // breaking the filter→request chain. The "[]" suffix is added only when
  // building the URL query string.
  useEffect(() => {
    const vals: FilterFields = {
      priceMin: searchParams.get('priceMin') ?? '',
      priceMax: searchParams.get('priceMax') ?? '',
      bedrooms: searchParams.get('bedrooms') ?? 'any',
    };
    for (const group of data.facetGroups ?? []) {
      vals[group.typeName] = searchParams.getAll(`${group.typeName}[]`);
    }
    reset(vals);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  function onApplyFilters(formData: FilterFields) {
    const next = new URLSearchParams(searchParams);

    if (formData.priceMin) next.set('priceMin', String(formData.priceMin));
    else next.delete('priceMin');

    if (formData.priceMax) next.set('priceMax', String(formData.priceMax));
    else next.delete('priceMax');

    if (formData.bedrooms && formData.bedrooms !== 'any') next.set('bedrooms', String(formData.bedrooms));
    else next.delete('bedrooms');

    for (const group of data.facetGroups ?? []) {
      const urlKey = `${group.typeName}[]`;
      next.delete(urlKey);
      const val = formData[group.typeName];
      if (val) {
        const arr = Array.isArray(val) ? val : [val];
        for (const v of arr) next.append(urlKey, v);
      }
    }

    setSearchParams(next);
  }

  function onClearFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete('priceMin');
    next.delete('priceMax');
    next.delete('bedrooms');
    for (const group of data.facetGroups ?? []) {
      next.delete(`${group.typeName}[]`);
    }
    setSearchParams(next);

    const cleared: FilterFields = { priceMin: '', priceMax: '', bedrooms: 'any' };
    for (const group of data.facetGroups ?? []) {
      cleared[group.typeName] = [];
    }
    reset(cleared);
  }

  if (!data.hasResults) return null;

  return (
    <div className="lg:sticky lg:top-24">
      <div
        className="lift p-6"
        style={{ background: 'var(--surface-lowest)', borderRadius: '1rem' }}
      >
        <div className="eyebrow mb-5">Refine</div>

        <form onSubmit={handleSubmit(onApplyFilters)} autoComplete="off" className="space-y-7">
          <div>
            <label className="eyebrow block mb-3">Price per night</label>
            <div className="grid grid-cols-2 gap-3">
              <input
                {...register('priceMin')}
                type="number"
                placeholder="Min $"
                className="field w-full"
              />
              <input
                {...register('priceMax')}
                type="number"
                placeholder="Max $"
                className="field w-full"
              />
            </div>
          </div>

          {data.facetGroups?.map((group) => {
            const isCollapsed = !!collapsedGroups[group.typeName];
            return (
              <div key={group.typeName}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.typeName)}
                  className="eyebrow flex items-center justify-between w-full text-left mb-3"
                  style={{ color: 'var(--on-secondary-container)' }}
                >
                  <span>{group.displayName}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-3 w-3 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    style={{ color: 'var(--on-surface-variant)' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {!isCollapsed && (
                  <div className="space-y-1.5">
                    {group.values.map((fv) => (
                      <label
                        key={fv.key}
                        className="flex items-center py-1.5 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          value={fv.key}
                          {...register(group.typeName)}
                          className="mr-3"
                          style={{
                            width: '1rem',
                            height: '1rem',
                            accentColor: 'var(--primary)',
                          }}
                        />
                        <span className="flex-1 text-sm" style={{ color: 'var(--on-surface)' }}>
                          {fv.label}
                        </span>
                        <span
                          className="text-xs"
                          style={{ color: 'var(--on-surface-variant)' }}
                        >
                          {fv.docCount}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <div>
            <label className="eyebrow block mb-3">Bedrooms</label>
            <div className="grid grid-cols-5 gap-2">
              {['any', '1', '2', '3', '4'].map((val) => (
                <label key={val} className="flex">
                  <input type="radio" value={val} {...register('bedrooms')} className="peer sr-only" />
                  <span
                    className="w-full text-center py-2 text-sm font-medium cursor-pointer transition-colors"
                    style={{
                      background: 'var(--surface-high)',
                      color: 'var(--on-surface)',
                      borderRadius: '9999px',
                    }}
                  >
                    {val === 'any' ? 'Any' : val}
                  </span>
                </label>
              ))}
            </div>
            <style>{`
              .editorial input[type="radio"]:checked + span {
                background: var(--primary) !important;
                color: #ffffff !important;
              }
            `}</style>
          </div>

          <div className="flex items-center gap-4 pt-2">
            <button type="submit" className="pill-primary flex-1 justify-center">
              Apply
            </button>
            <button
              type="button"
              onClick={onClearFilters}
              className="tertiary text-sm"
            >
              Clear all
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
